import { useEffect } from "react";
import { useTranslation } from "react-i18next";

import { LiveStreamCodeEnum } from "@api-types/event/LiveStreamCodeConstant";
import {
	LiveStreamFetchMode,
	LiveStreamResult,
} from "@api-types/event/LiveStreamResponse";
import { ErrorCodeMainEnum } from "@api-types/common/ErrorCodeConstants";

import { EventLiveStreamService } from "@services/event/EventLiveStreamService";
import { useLiveWidgetStore } from "@state/hooks/live-widgets";

import { useRootAppStore } from "@hooks";
import { WidgetLocation } from "@data-types/live-widget/WidgetLocation";
import { resolvePath } from "@utils/common";

export function useLiveStreamURLFetch(
	eventId: string,
	location: WidgetLocation
) {
	const store = useLiveWidgetStore();

	const t = useTranslation().t;
	const rootStore = useRootAppStore();
	const myWidget = store.findActiveLiveStreamWidget(eventId, location);

	const messageForCode = (code?: LiveStreamCodeEnum) => {
		if (
			code === LiveStreamCodeEnum.InvalidIPAddress ||
			code === LiveStreamCodeEnum.StreamGeorestricted ||
			code === LiveStreamCodeEnum.UnauthorizedIPAddress
		) {
			return t<string>("LIVE_WIDGETS.LIVE_STREAM.COUNTRY_IP_RESTRICTION");
		}

		return t<string>("LIVE_WIDGETS.LIVE_STREAM.STREAM_UNAVAILABLE");
	};

	const showError = (code?: LiveStreamCodeEnum) => {
		store.replaceWidgetMessage(myWidget!, {
			infoMessage: undefined,
			errorMessage: messageForCode(code),
		});
	};

	const playClientStream = async (result: LiveStreamResult) => {
		try {
			const res = await fetch(result.url);

			if (!res.ok) {
				const code =
					result.errorMap?.[res.status] ??
					LiveStreamCodeEnum.StreamUnavailable;
				showError(code);
				return;
			}

			const json = await res.json();
			const hlsUrl = resolvePath(json, result.responsePath ?? "");

			if (!hlsUrl) {
				showError(LiveStreamCodeEnum.StreamUnavailable);
				return;
			}

			myWidget!.url = hlsUrl;
		} catch (e) {
			showError(LiveStreamCodeEnum.StreamUnavailable);
		}
	};

	const playServerStream = async (url: string) => {
		try {
			const streamAvailability = await fetch(url);
			if (streamAvailability.status != 200) {
				throw "LIVE_WIDGETS.LIVE_STREAM.STREAM_UNAVAILABLE";
			}
		} catch (e) {
			showError(LiveStreamCodeEnum.StreamUnavailable);
		}

		myWidget!.url = url;
	};

	const onInitialize = async () => {
		try {
			if (!eventId || !myWidget || myWidget.url) {
				return;
			}

			const response =
				await EventLiveStreamService.getLiveStreamUrl(eventId);
			if (
				response.statusCode === ErrorCodeMainEnum.Success &&
				response.errorCode != null &&
				response.response != null
			) {
				const result = response.response;

				if (result.fetchMode === LiveStreamFetchMode.Client) {
					await playClientStream(result);
				} else {
					await playServerStream(result.url);
				}
			} else if (response.statusCode === ErrorCodeMainEnum.Error) {
				showError(response.errorCode);
			} else {
				store.addEventWithLsError(eventId);
				showError(LiveStreamCodeEnum.StreamUnavailable);
			}
		} catch (error) {
			showError(LiveStreamCodeEnum.StreamUnavailable);
		}
	};

	useEffect(() => {
		if (!rootStore.userAuthStore.isLoggedIn) {
			if (myWidget && myWidget.url) {
				myWidget.url = undefined;
			}
			return;
		}

		onInitialize();
		return () => {
			if (myWidget && myWidget.url) {
				myWidget.url = undefined;
			}
		};
	}, [myWidget, rootStore.userAuthStore.isLoggedIn]);

	return {
		myWidget,
		user: rootStore.userAuthStore,
	};
}
